import { Box } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { formatPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import {
  calculateRentMatchScore,
  getIncomeDocumentTitle,
} from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import {
  Activities,
  Banner,
  Button,
  Card,
  DisplayText,
  Error,
  ESpacings,
  Grid,
  Icon,
  Loading,
  Page,
  ResourceList,
  ResourceListItem,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import { IAction, TActions } from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import { InlineNotes } from "components/InlineNotes";
import { Rating } from "components/Rating";
import flatten from "lodash/flatten";
import React from "react";
import { useDispatch } from "react-redux";
import { StringParam } from "serialize-query-params";
import { ts } from "services";
import { ApplicationDetailHistory } from "../ApplicationDetailHistory";
import {
  ERentMatchBand,
  getRentMatchBand,
  getRentMatchBandLabelKey,
} from "../rentmatch.bands";
import { RM_COLORS } from "../rentmatch.colors";
import { buildScorePropertyDetails } from "../rentmatchScore.utils";
import * as applicationUtils from "./application.utils";
import {
  EPdfStatus,
  getContactActions,
  getViewActions,
} from "./applicationActions.utils";
import { EApplicationDetailTabs } from "./ApplicationDetail.types";
import {
  getResidentsCount,
  getScoreMoveInDate,
  getTotalIncome,
} from "./DragItem/dragItem.utils";
import { RentMatchWordmark } from "./RentMatchWordmark";

interface IProps {
  applicationId: string;
  onDrawerClose?: () => void;
}

const renderCardHeader = (heading: string) => (
  <DisplayText size="extraSmall" space="tight">
    {heading}
  </DisplayText>
);

// Rows that reach here always have a value. Empty ones are dropped before the
// list is built (DEV-3200), not here: this function is ResourceList's
// renderItem, so returning nothing from it would still leave the row wrapper
// and its separator standing.
const renderRow = (item: {
  title: string;
  value?: string | number;
  action?: IAction;
}) => (
  <ResourceListItem
    item={item}
    primaryActions={item.action ? [item.action] : undefined}
  >
    <TextStyle variation="subdued" element="div">
      {item.title}
    </TextStyle>
    <div style={{ whiteSpace: "pre-line" }}>{item.value}</div>
  </ResourceListItem>
);

export const ApplicationDetail = (props: IProps) => {
  const { applicationId } = props;
  const dispatch = useDispatch();

  const [, setQueryParamValue] = useQueryParams({
    applicationId: StringParam,
  });

  const [tab, setTab] = React.useState<EApplicationDetailTabs>(
    EApplicationDetailTabs.Info,
  );
  const [pdfStatus, setPdfStatus] = React.useState(EPdfStatus.Checking);

  const {
    detail: application,
    isFetching,
    fetchError,
  } = applicationHooks.useDetail({
    shouldRefetch: false,
    id: applicationId,
  });

  // History spans every re-submission for this candidate + property, so the broker keeps the
  // full trail instead of only the latest application.
  const { applicationIds } =
    applicationHooks.getCandidacyApplicationIds(application);

  const { items: propertyAmenities } = propertyAmenityHooks.useGetByPropertyId({
    propertyId: application?.propertyId,
  });

  // The office's income table lives on the broker, not on the property, so the
  // scoring needs it from here as well as from the property's own rows.
  const { broker } = authHooks.useSelf();

  // The score is computed from the property as the candidate list loads it, not
  // from the trimmed copy nested in the application. That nested copy carries
  // no costs and no availability date, so the same candidate scored differently
  // here than in the row that opened this panel.
  const { detail: scoredProperty } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: application?.propertyId,
  });

  React.useEffect(() => {
    const pdfUrlToCheck = application?.pdfUrl;
    if (!pdfUrlToCheck) {
      if (application) {
        setPdfStatus(EPdfStatus.NotAvailable);
      }
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Ranged GET (not HEAD): signed S3 URLs reject HEAD, and a plain GET
        // would download the whole PDF just to check existence. The Range
        // header fetches only the first byte (S3 answers 206 on success).
        const response = await fetch(pdfUrlToCheck as URL | RequestInfo, {
          method: "GET",
          headers: { Range: "bytes=0-1" },
        });
        if (cancelled) {
          return;
        }
        const isAvailable = response.status === 200 || response.status === 206;
        setPdfStatus(
          isAvailable ? EPdfStatus.Generated : EPdfStatus.Generating,
        );
      } catch {
        if (!cancelled) {
          setPdfStatus(EPdfStatus.NotAvailable);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [application]);

  // Render
  if (!application) {
    if (isFetching) {
      return <Loading />;
    }

    if (fetchError) {
      return <Error errors={[fetchError]} />;
    }

    return null;
  }

  const {
    contacts,
    dateOfOccupancy,
    reasonForRent,
    reasonForRentOther,
    currentResidenceAddress,
    financialRemarks,
    propertyId,
    property,
  } = application;
  const contact = contacts[0];

  const subtitle = applicationUtils.getSetInfo(application);

  const totalIncome = getTotalIncome(contacts);
  const adultCount = getResidentsCount(
    application.residents,
    "INTAKE_RESIDENT_ADULTS",
  );
  const childrenCount = getResidentsCount(
    application.residents,
    "INTAKE_RESIDENT_CHILDREN",
  );

  // Built from the property this panel loaded itself, all of it. Rent and
  // availability already came from there rather than from the trimmed copy
  // nested in the application; the type has to come from the same place, or the
  // scoring reads two different properties at once.
  const scorePropertyDetails = buildScorePropertyDetails(
    scoredProperty,
    propertyAmenities,
    broker,
  );

  const rentMatch =
    scorePropertyDetails.rent > 0
      ? calculateRentMatchScore(
          {
            netIncome: totalIncome,
            otherIncome: 0,
            adults: adultCount,
            minors: childrenCount,
            ...getScoreMoveInDate(dateOfOccupancy),
          },
          scorePropertyDetails,
        )
      : null;

  // Band and label come from rentmatch.bands.ts, the one definition shared with
  // the candidate row. Before that, this panel had its own copy: it missed the
  // perfect-match case and used a different key for the lowest band, so the
  // same candidate could read "High match" here and "Perfect match" in the row,
  // or "Low match" here and "No match" there. The colors stay here, because
  // this panel paints a ring where the row paints a pill.
  const getRentMatchLabel = (score: number) =>
    getLocalizedText(getRentMatchBandLabelKey(score));

  const getRentMatchColor = (score: number) => {
    switch (getRentMatchBand(score)) {
      case ERentMatchBand.Perfect:
      case ERentMatchBand.High:
        return RM_COLORS.green;
      case ERentMatchBand.Average:
        return RM_COLORS.amber;
      default:
        return RM_COLORS.red;
    }
  };

  const getRentMatchTextColor = (score: number) => {
    switch (getRentMatchBand(score)) {
      case ERentMatchBand.Perfect:
      case ERentMatchBand.High:
        return RM_COLORS.green;
      case ERentMatchBand.Average:
        return RM_COLORS.amberStrong;
      default:
        return RM_COLORS.red;
    }
  };

  const rentMatchNode = rentMatch ? (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div style={{ textAlign: "right" }}>
        <RentMatchWordmark height={13} />
        <div
          style={{
            color: RM_COLORS.gray500,
            fontSize: "11px",
            marginTop: "3px",
          }}
        >
          {getRentMatchLabel(rentMatch.score)}
        </div>
      </div>
      <div style={{ position: "relative", width: "42px", height: "42px" }}>
        <svg viewBox="0 0 36 36" style={{ width: "100%", height: "100%" }}>
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={RM_COLORS.gray200}
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={getRentMatchColor(rentMatch.score)}
            strokeWidth="3"
            strokeDasharray={`${rentMatch.score}, 100`}
            style={{ transition: "stroke-dasharray 0.5s ease" }}
          />
        </svg>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: "11px",
            color: getRentMatchTextColor(rentMatch.score),
          }}
        >
          {rentMatch.score}%
        </div>
      </div>
    </div>
  ) : null;

  const metadata = (
    <div>
      <Spacer weight={ESpacerWeight.W08} />
      {property && propertyId && (
        <Stack.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon
                source={utils.properties.getIcon(
                  property.typeId ||
                    // @ts-ignore
                    property.type ||
                    0,
                )}
                size="small"
              />
            </Grid.Item>
            <Grid.Item>
              <Button
                appearance="link"
                url={`/properties/${propertyId}/applications`}
              >
                {formatAddress(property)}
              </Button>
            </Grid.Item>
          </Grid>
        </Stack.Item>
      )}
    </div>
  );

  const handleInfoTabClick = () => {
    setTab(EApplicationDetailTabs.Info);
  };

  const handleHistoryTabClick = () => {
    setTab(EApplicationDetailTabs.History);
  };

  const handleActivityMoreClick = () => {
    setTab?.(EApplicationDetailTabs.History);
  };

  const dropdownActions: TActions = [
    getViewActions({
      item: application,
      pdfStatus,
      setQueryParamValue,
      isDetailView: true,
    }),
  ];

  let reasonForRentValue = "";
  if (reasonForRentOther) {
    reasonForRentValue = reasonForRentOther;
  } else if (reasonForRent) {
    reasonForRentValue = getLocalizedText(reasonForRent);
  }

  const sections: {
    title: string;
    subtitle: string;
    actions?: IAction[];
    items: {
      title: string;
      value?: string | number;
      action?: IAction;
    }[];
  }[] = [
    {
      title: getLocalizedText("tenant_profile.pdf.section.general"),
      subtitle: "",
      actions: getContactActions({
        item: application,
      }),
      items: [
        {
          title: getLocalizedText("system.full_name"),
          value: getName(contact),
        },
        { title: getLocalizedText("system.email"), value: contact.email },
        {
          title: getLocalizedText("system.mobile_number"),
          value: contact.phone ? formatPhone(contact.phone) : undefined,
        },
        {
          title: getLocalizedText("system.locale"),
          value: contact.language
            ? getLocalizedText(`system.locale.${contact.language}`)
            : undefined,
        },
      ],
    },
    {
      title: getLocalizedText("tenant_profile.pdf.section.residents"),
      subtitle: "",
      // No filter. The sender already withheld what a visit request may not
      // carry, so a pet entry that is here was allowed to travel, and this
      // panel showing something the list hides was the contradiction that
      // DEV-3135 removes.
      items: application.residents.map(({ id, value }) => ({
        title: getLocalizedText(id),
        value:
          value === 0 ? getLocalizedText(`${id}.empty`.toLowerCase()) : value,
      })),
    },
    {
      title: getLocalizedText("tenant_profile.pdf.section.rent_info"),
      subtitle: "",
      items: [
        {
          title: getLocalizedText("tenant_profile.pdf.item.date_of_occupancy"),
          value: dateOfOccupancy
            ? utils.date.format(dateOfOccupancy)
            : undefined,
        },
        {
          title: getLocalizedText("tenant_profile.pdf.item.reason_for_rent"),
          value: reasonForRentValue,
        },
        {
          title: getLocalizedText("tenant_profile.pdf.item.current_residence"),
          value: formatAddress(currentResidenceAddress),
        },
      ],
    },
    // All tenants
    ...contacts.map((contact, index: number) => ({
      title: `${getName(contact)}`,
      subtitle: "",
      actions: getContactActions({
        item: application,
        contact,
      }),
      items: [
        {
          title: contact.isGuarantor
            ? getLocalizedText("system.guarantor")
            : getLocalizedText("system.tenant"),
          value: getName(contact),
        },
        // TODO: @dries: add address to contact type
        // @ts-ignore
        {
          title: getLocalizedText("system.address"),
          // @ts-ignore
          value: contact.address ? formatAddress(contact.address) : "",
        },
        { title: getLocalizedText("system.email"), value: contact.email },
        {
          title: getLocalizedText("system.mobile_number"),
          value: contact.phone ? formatPhone(contact.phone) : undefined,
        },
        ...(contact.income || []).map(income => ({
          title: getLocalizedText(income.id),
          value: formatCurrency(income.value || 0, true),
        })),
        ...(index === 0
          ? [
              {
                title: getLocalizedText("system.comments"),
                value: financialRemarks,
              },
            ]
          : []),
      ],
    })),
    {
      title: getLocalizedText("system.comments_other"),
      subtitle: "",
      items: [{ title: getLocalizedText("system.comments"), value: "" }],
    },
    {
      title: getLocalizedText("system.documents"),
      subtitle: "",
      items: [
        // ID's
        ...flatten(
          contacts.map(contact =>
            (contact.documents || []).map(document => ({
              title: getLocalizedText("intake.contract.id.title"),
              value: getName(contact),
              action: {
                id: "open",
                content: getLocalizedText("document.open"),
                onClick: () => {
                  window.open(document, "_blank");
                },
              },
            })),
          ),
        ),
        // Income
        ...flatten(
          flatten(
            contacts.map(contact =>
              (contact.income || []).map(income =>
                (income.documents || []).map(document => ({
                  title: getIncomeDocumentTitle(income, formatCurrency),
                  value: getName(contact),
                  action: {
                    id: "open",
                    content: getLocalizedText("document.open"),
                    onClick: () => {
                      window.open(document, "_blank");
                    },
                  },
                })),
              ),
            ),
          ),
        ),
      ],
    },
  ];

  // Only what the candidate actually filled in is shown (DEV-3200). A field
  // with no value is left out instead of carrying a placeholder that says it
  // was not filled in, and a section that keeps nothing is left out with it,
  // so no titled card stands empty. Zero is not emptiness: the household block
  // turns a 0 into its own wording before it gets here, because a count of
  // none is an answer the candidate gave.
  const hasValue = (item: { value?: string | number }) =>
    item.value !== undefined && item.value !== null && `${item.value}` !== "";

  const visibleSections = sections
    .map(section => ({ ...section, items: section.items.filter(hasValue) }))
    .filter(section => section.items.length > 0);

  let tabs = [
    {
      name: "dashboard",
      permission: "propertyDashboard:visit",
      content: ts.paymentRequestDetailTabsInfo(),
      onClick: handleInfoTabClick,
      isActive: tab === EApplicationDetailTabs.Info,
    },
    {
      name: "payments",
      permission: "propertyPayments:visit",
      content: ts.paymentRequestDetailTabsHistory(),
      onClick: handleHistoryTabClick,
      isActive: tab === EApplicationDetailTabs.History,
    },
  ];

  const renderInfo = () => {
    const crossMeta = (application as any).otherApplicationsMeta;
    const crossNoShows = crossMeta?.noShows ?? [];
    const crossNotes = crossMeta?.notes ?? [];
    const hasCrossPropertyWarnings =
      crossNoShows.length > 0 || crossNotes.length > 0;

    const crossPropertyContent = hasCrossPropertyWarnings ? (
      <Box mt={ESpacings.base}>
        {crossNoShows.length > 0 && (
          <Banner
            variation="warning"
            hasDismiss={false}
            title={getLocalizedText("system.warning")}
          >
            <div style={{ marginBottom: "4px" }}>
              {getLocalizedText("application.cross_property.noshow.warning", {
                strongContent: getLocalizedText(
                  "application.cross_property.tooltip.noshow",
                ).toLowerCase(),
              })
                .split("<strong>")
                .map((part, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <strong>
                        {getLocalizedText(
                          "application.cross_property.tooltip.noshow",
                        ).toLowerCase()}
                      </strong>
                    )}
                    {part.replace("</strong>", "")}
                  </React.Fragment>
                ))}
            </div>
            {crossNoShows.map((ns: any, i: number) => (
              <div key={i} style={{ fontWeight: 600, marginTop: "2px" }}>
                {ns.propertyAddress}
                {" — "}
                {formatDate(new Date(ns.noShowAt), "d MMMM yyyy")}
              </div>
            ))}
          </Banner>
        )}
        {crossNotes.length > 0 && (
          <Box mt={crossNoShows.length > 0 ? ESpacings.base : undefined}>
            <Banner
              variation="info"
              hasDismiss={false}
              title={getLocalizedText("application.cross_property.notes.title")}
            >
              {crossNotes.map((n: any, i: number) => (
                <div
                  key={i}
                  style={{
                    marginTop: i > 0 ? "8px" : "0",
                    borderTop:
                      i > 0 ? `1px solid ${RM_COLORS.gray200}` : undefined,
                    paddingTop: i > 0 ? "8px" : undefined,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "12px",
                      color: RM_COLORS.gray500,
                    }}
                  >
                    {n.propertyAddress}
                  </div>
                  <div style={{ whiteSpace: "pre-line", marginTop: "2px" }}>
                    {n.content}
                  </div>
                </div>
              ))}
            </Banner>
          </Box>
        )}
      </Box>
    ) : null;

    return (
      <React.Fragment>
        <InlineNotes
          customPath={`/applications/${application.id}/notes`}
          // A candidacy carries several notes: what the broker wrote and, on
          // its own line, what each owner was shown.
          showAllNotes={true}
          customContentBefore={
            <Box mb={ESpacings.base}>
              <Rating
                value={application.rating || 0}
                onChange={value => {
                  dispatch(
                    applicationActions.setApplicationRatingStart.getAction({
                      id: application.id,
                      value,
                    }),
                  );
                }}
              />
            </Box>
          }
          customContentAfter={
            <>
              {application.noShowAt ? (
                <Box mt={ESpacings.base}>
                  <Banner
                    variation="warning"
                    title={getLocalizedText("system.warning")}
                  >
                    {getLocalizedText("application.no_show")}
                    {property && (
                      <div style={{ marginTop: "4px", fontWeight: 600 }}>
                        {formatAddress(property)}
                        {" — "}
                        {utils.date.format(new Date(application.noShowAt))}
                      </div>
                    )}
                  </Banner>
                </Box>
              ) : undefined}
              {crossPropertyContent}
            </>
          }
          cardProps={{
            heading: renderCardHeader(
              getLocalizedText("application.inline_notes.heading"),
            ),
          }}
        />

        {visibleSections.map(({ title, items, actions }) => (
          <Card
            key={title}
            heading={renderCardHeader(title)}
            subHeading={undefined}
            actions={actions}
            space={ESpacings.loose}
          >
            <ResourceList items={items} renderItem={renderRow} />
          </Card>
        ))}

        <Card
          heading={renderCardHeader(ts.system("history"))}
          space={ESpacings.base}
        >
          <Box mb={ESpacings.base}>
            <Activities
              identifier={`application-${application.id}-compact`}
              filter={{
                where: {
                  applicationId: { inq: applicationIds },
                },
              }}
              limit={3}
              hasFetchMore={false}
              isCompact={true}
              onMoreClick={handleActivityMoreClick}
            />
          </Box>
        </Card>
      </React.Fragment>
    );
  };

  const renderHistory = () => (
    <ApplicationDetailHistory application={application} setTab={setTab} />
  );

  return (
    <Page
      title={getName(contact)}
      subtitle={subtitle}
      metadata={metadata}
      tabs={tabs}
      dropdown={dropdownActions}
      secondaryActionNode={rentMatchNode}
    >
      {tab === EApplicationDetailTabs.Info && renderInfo()}
      {tab === EApplicationDetailTabs.History && renderHistory()}
    </Page>
  );
};
